import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  SimpleGrid,
  useColorMode,
  Image,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../brandConfig";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const BACKGROUND_IMAGE_URL = "/Photos/Rock-4.jpg";

const InvestmentProducts = () => {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();

  // Theme-aware colors
  const bg = getColor("background.main", colorMode);

  // Preload background image
  useEffect(() => {
    const img = document.createElement('img');
    img.src = BACKGROUND_IMAGE_URL;
    img.onload = () => {
      console.log('Background image preloaded');
    };
  }, []);

  return (
    <Box
      bg={bg}
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      sx={{
        overscrollBehaviorY: "none",
        WebkitOverflowScrolling: "touch",
        "@keyframes fadeInUp": {
          "0%": {
            opacity: 0,
            transform: "translateY(30px)"
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)"
          }
        }
      }}
    >
      <NavbarWithCallToAction />

      {/* Fixed Background Image Container with optimized loading */}
      <Box
        position="fixed"
        top="0"
        left="0"
        width="100%"
        height="100%"
        zIndex={0}
        backgroundImage={`url(${BACKGROUND_IMAGE_URL})`}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        backgroundColor="#1a1a1a"
        sx={{
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 1,
          }
        }}
      />

      {/* Scrolling Content Container */}
      <Box position="relative" zIndex={1}>
        {/* Page Header */}
        <Box
          bg="transparent"
          position="relative"
          pt={{ base: 24, md: 28, lg: 32 }}
          pb={{ base: 8, md: 12, lg: 16 }}
          textAlign="center"
        >
          <Container maxW="1400px" px={{ base: 6, md: 8 }}>
            <VStack spacing={8}>
              <Box
                pt={16}
                position="relative"
                opacity={0}
                transform="translateY(30px)"
                animation="fadeInUp 1.0s ease-out 0.3s forwards"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: '-10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '80px',
                  height: '2px',
                  bg: 'linear-gradient(90deg, transparent, #FF6600, transparent)',
                  borderRadius: 'full'
                }}
              >
                <Heading
                  as="h1"
                  fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                  fontWeight="700"
                  color="#FFFFFF"
                  fontFamily={getBrandValue("fonts.heading")}
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                  mb={4}
                >
                  Investment Products
                </Heading>
              </Box>
            </VStack>
          </Container>
        </Box>

        {/* Investment Products Content */}
        <Box
          bg="transparent"
          position="relative"
          py={{ base: 12, md: 16, lg: 20 }}
        >
          <Container maxW="1400px" px={{ base: 6, md: 8, lg: 12 }}>
            {/* Vertical Stack Layout for All Screen Sizes */}
            <VStack spacing={{ base: 10, md: 14, lg: 16 }}>

              {/* Providence Aged Care Fund I */}
              <Box
                w="100%"
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  bg: 'linear-gradient(90deg, #FF6600, rgba(255, 102, 0, 0.5), transparent)',
                  borderRadius: 'full'
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '100px',
                  height: '100px',
                  bg: 'radial-gradient(circle, rgba(255, 102, 0, 0.1), transparent)',
                  borderRadius: 'full',
                  pointerEvents: 'none'
                }}
              >
                <Box
                  bg="rgba(255, 255, 255, 0.03)"
                  p={10}
                  borderRadius="lg"
                  border="1px solid rgba(255, 102, 0, 0.2)"
                  backdropFilter="blur(15px)"
                  boxShadow="0 8px 32px rgba(255, 102, 0, 0.1)"
                >
                  <VStack spacing={6} align="start">
                    <HStack spacing={6} align="center">
                      <Box
                        width="4px"
                        height="60px"
                        bg="linear-gradient(180deg, #FF6600, rgba(255, 102, 0, 0.5))"
                        borderRadius="full"
                        boxShadow="0 0 10px rgba(255, 102, 0, 0.3)"
                      />
                      <VStack align="start" spacing={2}>
                        <Heading
                          as="h2"
                          fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                          fontWeight="600"
                          color="#FFFFFF"
                          fontFamily={getBrandValue("fonts.heading")}
                          textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                        >
                          Providence Aged Care Fund I
                        </Heading>
                        <Text
                          fontSize="sm"
                          color="#FF6600"
                          fontFamily={getBrandValue("fonts.body")}
                          fontWeight="500"
                          letterSpacing="0.5px"
                          textTransform="uppercase"
                        >
                          Healthcare Infrastructure Investment
                        </Text>
                      </VStack>
                    </HStack>
                    <Box pl={{ base: 0, md: 10 }}>
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="rgba(255, 255, 255, 0.9)"
                        lineHeight="1.8"
                        fontFamily={getBrandValue("fonts.body")}
                        textShadow="1px 1px 3px rgba(0,0,0,0.7)"
                      >
                        A specialized investment vehicle focused on premium aged care facilities and healthcare infrastructure.
                        This fund targets high-quality residential aged care assets that provide essential services while generating
                        stable, long-term returns for investors committed to supporting Australia's aging population.
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </Box>

              {/* Thrive Cash Management */}
              <Box
                w="100%"
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  bg: 'linear-gradient(90deg, #AAC3FF, rgba(148, 206, 235, 0.5), transparent)',
                  borderRadius: 'full'
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '100px',
                  height: '100px',
                  bg: 'radial-gradient(circle, rgba(148, 206, 235, 0.1), transparent)',
                  borderRadius: 'full',
                  pointerEvents: 'none'
                }}
              >
                <Box
                  bg="rgba(255, 255, 255, 0.03)"
                  p={10}
                  borderRadius="lg"
                  border="1px solid rgba(148, 206, 235, 0.2)"
                  backdropFilter="blur(15px)"
                  boxShadow="0 8px 32px rgba(148, 206, 235, 0.1)"
                >
                  <VStack spacing={6} align="start">
                    <HStack spacing={6} align="center">
                      <Box
                        width="4px"
                        height="60px"
                        bg="linear-gradient(180deg, #AAC3FF, rgba(148, 206, 235, 0.5))"
                        borderRadius="full"
                        boxShadow="0 0 10px rgba(148, 206, 235, 0.3)"
                      />
                      <VStack align="start" spacing={2}>
                        <Heading
                          as="h2"
                          fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                          fontWeight="600"
                          color="#FFFFFF"
                          fontFamily={getBrandValue("fonts.heading")}
                          textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                        >
                          Thrive Cash Management
                        </Heading>
                        <Text
                          fontSize="sm"
                          color="#AAC3FF"
                          fontFamily={getBrandValue("fonts.body")}
                          fontWeight="500"
                          letterSpacing="0.5px"
                          textTransform="uppercase"
                        >
                          Dynamic Liquidity Solution
                        </Text>
                      </VStack>
                    </HStack>
                    <Box pl={{ base: 0, md: 10 }}>
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="rgba(255, 255, 255, 0.9)"
                        lineHeight="1.8"
                        fontFamily={getBrandValue("fonts.body")}
                        textShadow="1px 1px 3px rgba(0,0,0,0.7)"
                      >
                        A dynamic cash management solution offering flexible liquidity management with competitive returns.
                        Designed for institutional and sophisticated investors seeking capital preservation and easy access to their funds.
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </Box>

              {/* Providence Private Credit Trust I */}
              <Box
                w="100%"
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  bg: 'linear-gradient(90deg, #F1F2F2, rgba(241, 242, 242, 0.5), transparent)',
                  borderRadius: 'full'
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '100px',
                  height: '100px',
                  bg: 'radial-gradient(circle, rgba(241, 242, 242, 0.1), transparent)',
                  borderRadius: 'full',
                  pointerEvents: 'none'
                }}
              >
                <Box
                  bg="rgba(255, 255, 255, 0.03)"
                  p={10}
                  borderRadius="lg"
                  border="1px solid rgba(241, 242, 242, 0.2)"
                  backdropFilter="blur(15px)"
                  boxShadow="0 8px 32px rgba(241, 242, 242, 0.1)"
                >
                  <VStack spacing={6} align="start">
                    <HStack spacing={6} align="center">
                      <Box
                        width="4px"
                        height="60px"
                        bg="linear-gradient(180deg, #F1F2F2, rgba(241, 242, 242, 0.5))"
                        borderRadius="full"
                        boxShadow="0 0 10px rgba(241, 242, 242, 0.3)"
                      />
                      <VStack align="start" spacing={2}>
                        <Heading
                          as="h2"
                          fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                          fontWeight="600"
                          color="#FFFFFF"
                          fontFamily={getBrandValue("fonts.heading")}
                          textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                        >
                          Providence Private Credit Trust I
                        </Heading>
                        <Text
                          fontSize="sm"
                          color="#F1F2F2"
                          fontFamily={getBrandValue("fonts.body")}
                          fontWeight="500"
                          letterSpacing="0.5px"
                          textTransform="uppercase"
                        >
                          Exclusive Private Credit Strategy
                        </Text>
                      </VStack>
                    </HStack>
                    <Box pl={{ base: 0, md: 10 }}>
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="rgba(255, 255, 255, 0.9)"
                        lineHeight="1.8"
                        fontFamily={getBrandValue("fonts.body")}
                        textShadow="1px 1px 3px rgba(0,0,0,0.7)"
                      >
                        An exclusive private credit strategy providing direct lending solutions to established businesses and growth
                        enterprises. This trust targets middle-market companies with proven business models, offering attractive
                        risk-adjusted returns through senior and subordinated debt instruments with robust security structures.
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              </Box>

              {/* Call-to-Action Section */}
              <Box
                w="100%"
                textAlign="center"
                py={8}
              >
                <VStack spacing={6}>
                  <Text
                    fontSize={{ base: "lg", md: "xl" }}
                    color="#FFFFFF"
                    fontFamily={getBrandValue("fonts.body")}
                    fontWeight="500"
                    textShadow="1px 1px 3px rgba(0,0,0,0.7)"
                  >
                    Ready to explore our investment opportunities?
                  </Text>

                  <Button
                    size="lg"
                    bg="#FF6600"
                    color="#FFFFFF"
                    _hover={{
                      bg: "#CC4902",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(255, 102, 0, 0.4)"
                    }}
                    onClick={() => navigate("/contact-us")}
                    px={16}
                    py={6}
                    fontSize="md"
                    borderRadius="sm"
                    transition="all 0.3s ease"
                    fontFamily={getBrandValue("fonts.body")}
                    fontWeight="500"
                    letterSpacing="1px"
                    textTransform="uppercase"
                    minW="200px"
                  >
                    Contact Us for More Info
                  </Button>
                </VStack>
              </Box>

            </VStack>
          </Container>
        </Box>

        {/* Extra space to ensure smooth scrolling */}
        <Box minHeight="20vh" />
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default InvestmentProducts;